// src/svelte/actions/focusTrap.js
//
// Keeps keyboard focus inside a modal. The match modals block the MOUSE with a
// fixed-position z-index overlay, but Tab still walked into the scoring pad
// underneath — a scorer could Tab to "WICKET" and press Enter while the bowler
// picker was open and corrupt the match state.
//
// Usage:  <div class="modal-overlay" use:focusTrap>
//
// Plain JS, no dependencies. Restores focus to the previously-focused element
// on teardown so closing a modal returns the user where they were.

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

function focusableWithin(node) {
  return Array.from(node.querySelectorAll(FOCUSABLE))
    .filter(el => el.offsetParent !== null || el === document.activeElement);
}

/**
 * @param {HTMLElement} node
 * @param {{ onEscape?: () => void }} [options]
 */
export function focusTrap(node, options = {}) {
  const previouslyFocused = document.activeElement;
  let opts = options;

  function onKeydown(event) {
    if (node.closest('[inert]')) return;
    if (event.key === 'Escape' && typeof opts.onEscape === 'function') {
      event.preventDefault();
      opts.onEscape();
      return;
    }
    if (event.key !== 'Tab') return;

    const items = focusableWithin(node);
    if (items.length === 0) {
      event.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;

    // Focus escaped the modal entirely (or is on the overlay itself) — pull it back.
    if (!node.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // Move focus into the modal so the very first Tab is already trapped.
  const initial = focusableWithin(node)[0];
  if (initial) {
    initial.focus();
  } else {
    node.setAttribute('tabindex', '-1');
    node.focus();
  }

  document.addEventListener('keydown', onKeydown, true);

  return {
    update(next = {}) { opts = next; },
    destroy() {
      document.removeEventListener('keydown', onKeydown, true);
      if (previouslyFocused instanceof HTMLElement && document.contains(previouslyFocused)) {
        try { previouslyFocused.focus(); } catch (_) {}
      }
    }
  };
}
